/**
 * RouteFallback — the Suspense fallback shown while a lazy-loaded route chunk
 * is fetched (#1472 route-level code splitting).
 *
 * Mirrors {@link ProtectedRoute}'s loading idiom (design-system tokens only:
 * `bg-background`, `border-hairline` / `border-t-primary-600`,
 * `text-foreground-muted`) so a chunk fetch on a throttled connection reads as
 * a normal in-app load rather than a flash of unstyled content. It is a
 * full-viewport centered indicator because it stands in for an entire route.
 *
 * `role="status"` + `aria-live="polite"` announces the transient load to
 * assistive tech; the spinner itself is `aria-hidden` (the text carries the
 * meaning). Purely presentational — no props, no data, no user input.
 * @returns Centered, accessible loading indicator.
 */
export function RouteFallback(): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen items-center justify-center bg-background"
    >
      <div className="text-center">
        <div
          className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-hairline border-t-primary-600"
          aria-hidden="true"
        />
        <p className="mt-4 text-foreground-muted">Loading…</p>
      </div>
    </div>
  );
}
